import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/react';
import * as NativeSentry from '@sentry/capacitor';

const native = Capacitor.isNativePlatform();
// The native SDK swaps in a native transport keyed on platform alone; if the
// pod is not actually linked, every envelope then throws and reporting goes
// dark. Only take the native path when the plugin is really present.
const nativePluginLinked = native && Capacitor.isPluginAvailable('SentryCapacitor');
const dsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND;

function initialize() {
  if (dsn) {
    const buildTags = {
      platform: native ? 'ios-app' : 'web',
      build_commit: __BUILD_INFO__?.commit ?? 'development',
      build_config: __BUILD_INFO__?.configHash ?? 'development',
      build_purpose: __BUILD_INFO__?.purpose ?? 'development',
    };
    const options: Sentry.BrowserOptions = {
      dsn,
      tracesSampleRate: 0,
      release: import.meta.env.VITE_SENTRY_RELEASE,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === 'true',
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        /Network request failed/,
        /Load failed/,
        /ChunkLoadError/,
        /Loading chunk .* failed/,
      ],
      initialScope: { tags: buildTags },
      debug: import.meta.env.DEV,
    };
    if (nativePluginLinked) {
      // Use the same React/core client for existing captureException/setUser calls,
      // with Capacitor providing native transport, scope sync and crash handling.
      NativeSentry.init({
        ...options,
        enableNative: true,
        enableNativeCrashHandling: true,
        enableNativeNagger: false,
      }, Sentry.init);
      // Capacitor drops initialScope from native init options. Its init hooks
      // the isolation scope's setters, so explicitly send the same tags after
      // initialization for native crashes as well as JavaScript errors.
      Sentry.setTags(buildTags);
    } else {
      Sentry.init(options);
      if (native) {
        // A native build without the linked pod means crash capture is absent;
        // report it through the working browser transport instead of dying silent.
        Sentry.captureMessage('SentryCapacitor plugin unavailable in native build; native crash capture is OFF', 'error');
      }
    }
  } else {
    console.warn('Sentry DSN not configured. Production builds reject this configuration.');
  }
}

try {
  initialize();
} catch (error) {
  // Monitoring must not prevent the application from opening. Release device
  // acceptance checks the client and native plugin as well as event ingestion.
  console.error('Sentry initialization failed:', error);
}

// Deliberate developer-console diagnostics, not an automatic event or a user
// flow. A flush acknowledges queue processing; verify the returned ID in Sentry.
// NATIVE ONLY: the WebView console is reachable only through a connected Mac,
// while on the public website this object would hand any injected script a
// Sentry-quota burner. Web acceptance does not use these diagnostics.
// The whole block is best-effort: a hostile pre-existing property must not
// stop the app from booting (this module is the first one main.tsx evaluates).
try {
  if (native) {
    Object.defineProperty(window, '__FF_DIAGNOSTICS__', {
      configurable: true,
      value: Object.freeze({
        build: __BUILD_INFO__,
        nativeSentryAvailable: nativePluginLinked,
        async captureSentryTest() {
          if (!Sentry.getClient()) throw new Error('Sentry has not initialized');
          const marker = crypto.randomUUID();
          const eventId = Sentry.captureException(new Error(`Release verification ${marker}`), {
            tags: { release_verification: marker },
          });
          const flushed = await Sentry.flush(5000);
          return { eventId, marker, flushed, build: __BUILD_INFO__ };
        },
        crashNativeForVerification() {
          if (!nativePluginLinked) throw new Error('Native Sentry plugin unavailable');
          NativeSentry.nativeCrash();
        },
      }),
    });
  }
} catch (error) {
  console.error('Diagnostics installation failed:', error);
}
